import React from "react";

/** Los bloqueos de la entrega dichos en simple */
const BLOQUEOS = {
  SIN_INSTRUCCION_ENTREGA: "Esperando la placa, el conductor, la cédula y el celular (o que se elija el transportador)",
  TRANSPORTADOR_SIN_CARTA: "El transportador no tiene carta de autorización",
};
/** Tono del chip según el estado de la tarea */
const TONO_TAREA = { 100: "chip-ambar", 107: "chip-gris", 101: "chip-gris", 103: "chip-verde", 105: "chip-verde" };

/**
 * Bloqueo, instrucción, tarea y actas de la entrega, en frases cortas.
 * @param {object} props
 * @param {object} props.foto Respuesta de /laboratorio/quePaso
 * @author Claude Code (Alexandra Parra) <direccionit.siaco@repremundo.com.co>
 * @since 2026-10-26
 */
const Qpentreg = ({ foto }) => (
  <div className="sim-qp-grid">
    <Qpoperac foto={foto} />
    <Qpbloque bloqueos={foto.BLOQUEOS ?? []} instruccion={foto.INSTRUCCION ?? {}} />
    <Qptareas tareas={foto.TAREAS ?? []} />
    <Qpactasx actas={foto.ACTAS ?? []} />
  </div>
);

/**
 * La operación y su levante.
 * @param {object} props
 * @param {object} props.foto Foto completa
 * @returns {JSX.Element}
 */
const Qpoperac = ({ foto }) => {
  const op = foto.OPERACION ?? {};
  const levantes = (foto.DECLARACIONES ?? []).filter((m) => m.LIMLEVXX && m.LIMLEVXX !== "SIN LEVANTE");
  const aceptadas = (foto.DECLARACIONES ?? []).filter((m) => m.LIMACEXX);
  const especial = foto.LEVANTEESPECIAL ?? {};
  return (
    <div className="sim-qp-caja">
      <div className="sim-qp-frase">{`DO ${op.DOCNROXX} · ${op.CLINOMXX || op.CLIIDXXX} · ${op.SUCIDXXX}`}</div>
      <span>{`Etapa: ${op.DOCETAPA || "—"}`}</span>
      <span>{`Declaraciones: ${(foto.DECLARACIONES ?? []).length}, aceptadas ${aceptadas.length}, con levante ${levantes.length}.`}</span>
      {levantes.map((m) => <span key={m.SUBIDXXX} className="sim-qp-nota">{`Levante ${m.LIMTLEVX} ${m.LIMLEVXX} del ${m.LIMFLEVX}`}</span>)}
      {especial.NUMERO ? <span className="rio-chip chip-verde">{`Levante especial ${especial.NUMERO} del ${especial.FECHA}`}</span> : null}
    </div>
  );
};

/**
 * Bloqueos de la entrega (abiertos y resueltos) y lo que falta de la instrucción.
 * @param {object} props
 * @param {Array<object>} props.bloqueos Filas {MDWATIPX, MDWAESTX, MDWAMSGX, REGFECMX, REGHORMX}
 * @param {object} props.instruccion {APLICA, MOTIVO, FALTAN, COMPLETA, SINTAREA, ERROR}
 * @returns {JSX.Element}
 */
const Qpbloque = ({ bloqueos, instruccion }) => {
  const abiertos = bloqueos.filter((m) => m.MDWAESTX === "ABIERTO");
  return (
    <div className="sim-qp-caja">
      <div className="sim-qp-frase">{abiertos.length ? "La entrega está BLOQUEADA" : "La entrega no está bloqueada"}</div>
      {bloqueos.map((m, i) => (
        <span key={`${m.MDWATIPX}-${i}`} className={`rio-chip ${m.MDWAESTX === "ABIERTO" ? "chip-rojo" : "chip-verde"}`}>
          {`${m.MDWAESTX === "ABIERTO" ? "Abierto" : "Resuelto"}: ${BLOQUEOS[m.MDWATIPX] ?? m.MDWATIPX}`}
        </span>
      ))}
      <span>{fraseInstruccion(instruccion)}</span>
    </div>
  );
};

/**
 * La instrucción de entrega en una frase.
 * @param {object} ins Estado de la instrucción
 * @returns {string}
 */
const fraseInstruccion = (ins) => {
  if (ins.ERROR) return `No se pudo leer la instrucción: ${ins.ERROR}`;
  if (ins.APLICA === false) return `No se pide instrucción para esta operación${ins.MOTIVO ? ` (${ins.MOTIVO})` : ""}.`;
  if (ins.COMPLETA) return "La instrucción de entrega está completa.";
  const falta = (ins.FALTAN ?? []).join(", ");
  return `Instrucción de entrega: ${falta ? `falta ${falta}` : "aún no llega"}${ins.SINTAREA ? ` (${ins.SINTAREA})` : ""}.`;
};

/**
 * Tareas de entrega al transportador en cualquier estado.
 * @param {object} props
 * @param {Array<object>} props.tareas {TARIDXXX, ESTAIDXX, ESTADESC, USRNOMXX, TARUSRRE, TARCPRIO, REGFECXX}
 * @returns {JSX.Element}
 */
const Qptareas = ({ tareas }) => (
  <div className="sim-qp-caja">
    <div className="sim-qp-frase">{tareas.length ? "Tarea de entrega al transportador" : "Todavía no hay tarea de entrega"}</div>
    {tareas.map((m) => (
      <div key={m.TARIDXXX}>
        <span className={`rio-chip ${TONO_TAREA[m.ESTAIDXX] ?? "chip-gris"}`}>{`${m.ESTADESC} (${m.ESTAIDXX})`}</span>
        <div className="sim-qp-nota">{`Tarea ${m.TARIDXXX} · responsable ${m.USRNOMXX || m.TARUSRRE || "sin asignar"} · prioridad ${m.TARCPRIO || "—"} · creada ${m.REGFECXX} ${m.REGHORXX}`}</div>
      </div>
    ))}
  </div>
);

/**
 * Actas de entrega emitidas y si ya se entregaron.
 * @param {object} props
 * @param {Array<object>} props.actas {AEMIDXXX, AEMPLACX, AEMCONDX, AEMFECRE, AEMHORRE, AEMRECNO}
 * @returns {JSX.Element}
 */
const Qpactasx = ({ actas }) => (
  <div className="sim-qp-caja">
    <div className="sim-qp-frase">{actas.length ? `Actas emitidas: ${actas.length}` : "Aún no se ha emitido el acta"}</div>
    {actas.map((m) => {
      const entregada = m.AEMFECRE && m.AEMFECRE !== "0000-00-00";
      return (
        <div key={m.AEMIDXXX}>
          <span className={`rio-chip ${entregada ? "chip-verde" : "chip-ambar"}`}>{entregada ? `Entregada ${m.AEMFECRE} ${m.AEMHORRE}` : "Emitida, sin entregar"}</span>
          <div className="sim-qp-nota">{`Acta ${m.AEMIDXXX} · placa ${m.AEMPLACX || "—"} · conductor ${m.AEMCONDX || "—"}${m.AEMRECNO ? ` · recibió ${m.AEMRECNO}` : ""}`}</div>
        </div>
      );
    })}
  </div>
);

export default Qpentreg;
